import React, { useState } from 'react'
import { Alert, Badge, Button, Card, Form } from 'react-bootstrap'
import Select from 'react-select'

import ProviderService from '../../../services/ProviderService'

export default function UnassignedClinicianCard({ item, orgOptions = [], onResolvedChange }) {
  const [selectedOrg, setSelectedOrg] = useState(() => {
    if (item.suggestedOrgId) {
      return {
        id: item.suggestedOrgId,
        label: item.suggestedOrgName,
        value: item.suggestedOrgName,
      }
    }
    return null
  })

  const [role, setRole] = useState('Primary Practice')
  const [isApplying, setIsApplying] = useState(false)
  const [isUndoing, setIsUndoing] = useState(false)
  const [error, setError] = useState(null)
  const [resolvedState, setResolvedState] = useState(null)

  const handleAffiliate = async () => {
    if (!selectedOrg?.id) {
      setError('Please select an organization / facility to affiliate with.')
      return
    }
    setIsApplying(true)
    setError(null)
    try {
      const response = await ProviderService.assignAffiliation({
        individualId: item.providerId,
        organizationId: selectedOrg.id,
        role: role.trim() || 'Primary Practice',
        isPrimary: true,
      })

      if (response && response.success) {
        setResolvedState({
          actionType: 'assign-affiliation',
          undoActionId: response.undoActionId,
          undoPayloadJson: response.undoPayloadJson || response.undoActionId,
          message: response.message || `Affiliated with ${selectedOrg.value || selectedOrg.label}.`,
        })
        if (onResolvedChange) onResolvedChange(item.providerId, true)
      } else {
        setError(response?.message || 'Failed to assign affiliation.')
      }
    } catch (err) {
      setError(err?.message || 'Error occurred while assigning affiliation.')
    } finally {
      setIsApplying(false)
    }
  }

  const handleUndo = async () => {
    if (!resolvedState?.undoPayloadJson && !resolvedState?.undoActionId) return
    setIsUndoing(true)
    setError(null)
    try {
      await ProviderService.undoLibraryAction({
        actionType: resolvedState.actionType,
        undoActionId: resolvedState.undoActionId,
        previousStateJson: resolvedState.undoPayloadJson,
      })
      setResolvedState(null)
      if (onResolvedChange) onResolvedChange(item.providerId, false)
    } catch (err) {
      setError(err?.message || 'Failed to undo affiliation.')
    } finally {
      setIsUndoing(false)
    }
  }

  const handleKeepIndependent = () => {
    setResolvedState({
      actionType: 'dismiss',
      undoPayloadJson: null,
      message: 'Kept as an independent solo practitioner.',
    })
    if (onResolvedChange) onResolvedChange(item.providerId, true)
  }

  return (
    <Card className={`mb-3 border-${resolvedState ? 'success' : 'light'} shadow-sm`}>
      <Card.Header className="d-flex justify-content-between align-items-center bg-white py-2">
        <div className="d-flex align-items-center">
          <span className="mr-2" role="img" aria-label="unassigned">
            🔗
          </span>
          <strong className="text-dark">Unaffiliated Individual</strong>
          {item.suggestedOrgName && (
            <Badge variant="success" className="ml-2 font-weight-normal">
              Organization Match Found
            </Badge>
          )}
        </div>
        {resolvedState && (
          <Badge variant="success" className="p-1 px-2">
            ✓ Resolved
          </Badge>
        )}
      </Card.Header>
      <Card.Body className="py-2">
        {error && (
          <Alert variant="danger" className="py-1 small mb-2">
            {error}
          </Alert>
        )}

        {resolvedState ? (
          <div className="d-flex justify-content-between align-items-center py-2">
            <span className="text-success font-weight-bold">✓ {resolvedState.message}</span>
            {resolvedState.undoPayloadJson && (
              <Button variant="outline-secondary" size="sm" onClick={handleUndo} disabled={isUndoing}>
                {isUndoing ? 'Undoing...' : 'Undo'}
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-start mb-2">
              <div>
                <h6 className="mb-0 font-weight-bold text-dark">{item.clinicianName}</h6>
                <div className="small text-muted">
                  {item.specialties && <span>{item.specialties} • </span>}
                  {item.address && <span>{item.address} • </span>}
                  {item.phone && <span>📞 {item.phone}</span>}
                </div>
              </div>
              <Badge variant="secondary">Independent</Badge>
            </div>

            <div className="bg-light p-2 rounded mb-2 border">
              {item.matchReason && (
                <div className="small text-success mb-2 font-weight-bold">💡 {item.matchReason}</div>
              )}
              <Form.Group controlId={`affilOrg-${item.providerId}`} className="mb-2">
                <Form.Label className="small mb-0 text-muted font-weight-bold">
                  Select Organization to Affiliate
                </Form.Label>
                <Select
                  isClearable
                  placeholder="Search existing organization..."
                  options={orgOptions}
                  value={selectedOrg}
                  onChange={val => setSelectedOrg(val)}
                  className="small"
                />
              </Form.Group>
              <Form.Group controlId={`affilRole-${item.providerId}`} className="mb-0">
                <Form.Label className="small mb-0 text-muted">Practice Role</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={role}
                  onChange={e => setRole(e.target.value)}
                  placeholder="Primary Practice, Attending Physician, Staff..."
                />
              </Form.Group>
            </div>

            <div className="d-flex justify-content-end align-items-center mt-2">
              <Button
                variant="outline-secondary"
                size="sm"
                className="mr-2"
                onClick={handleKeepIndependent}
                disabled={isApplying}
              >
                Keep Independent
              </Button>
              <Button variant="primary" size="sm" onClick={handleAffiliate} disabled={isApplying || !selectedOrg?.id}>
                {isApplying ? 'Affiliating...' : `Affiliate with ${selectedOrg?.value || 'Selected Organization'}`}
              </Button>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  )
}
